import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import styled from "styled-components";
import { v5 } from "@credijusto/minerals";
import AuthLayout from "../AuthLayout/AuthLayout";
import { useHistory } from "react-router-dom";
import { useAuth } from "../AuthContext/AuthContext";
import styles from "../LoginForm/LoginForm.module.css";

const Card = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0;

  @media screen and (min-width: 640px) {
    max-width: 45rem;
    margin-left: 0;
  }
  @media screen and (min-width: 768px) {
    max-width: 25rem;
    padding: 1rem;
    margin-left: 0;
  }
  @media screen and (min-width: 1024px) {
    max-width: 42rem;
    padding: var(--mn-space-700) var(--mn-space-900);
    margin-left: 0;
    padding: 3.5rem;
  }
  @media screen and (min-width: 1440px) {
    max-width: 58rem;
    padding: var(--mn-space-1000) var(--mn-space-1000);
    margin-left: 0;
    padding: 3.5rem;
  }
`;

const OTPContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: var(--mn-space-50);
  margin-top: var(--mn-space-100);
  margin-bottom: var(--mn-space-400);
  width: 100%;
  overflow-x: auto;

  @media screen and (min-width: 640px) {
    gap: var(--mn-space-200);
    margin-bottom: var(--mn-space-500);
    overflow-x: visible;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;

  @media screen and (max-width: 639px) {
    flex-direction: column-reverse;
    gap: 0.5rem;
    & > button {
      width: 100%;
    }
  }

  @media screen and (min-width: 640px) {
    margin-top: 3rem;
    flex-direction: row;
    align-items: flex-start;
  }
`;

const EmailContainer = styled.div`
  margin: 1.5rem 0;
  text-align: left;
  word-break: break-word;
  max-width: 85%;
  width: fit-content;

  @media screen and (max-width: 639px) {
    max-width: 80%;
  }

  @media screen and (min-width: 640px) {
    max-width: 90%;
  }

  @media screen and (min-width: 768px) {
    max-width: 100%;
  }
`;

const EmailText = styled.span`
  font-size: 1rem;
  font-weight: bold;
  color: var(--mn-color-text-primary);
  display: inline-block;
  max-width: 100%;
  overflow-wrap: break-word;
  text-align: left;
`;

const CorrectionText = styled.div`
  font-size: 0.875rem;
  margin-top: 0.5rem;
  color: var(--mn-color-text-secondary);
  text-align: left;
  max-width: 100%;
`;

const CorrectionLink = styled.span`
  color: var(--mn-color-primary);
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    text-decoration: none;
  }
`;

const CountdownText = styled.span`
  color: var(--mn-color-text-secondary);
  font-size: 0.875rem;
  margin-left: 0.5rem;
`;

const CheckboxContainer = styled.div`
  margin: 1.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

// Estilos iguales a la primera vista
const AutorizeBureu = styled.a`
  font-weight: bold;
  color: mn-secondary;
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  cursor: pointer;
`;

const CheckboxWrapper = styled.div`
  width: 100%;
  max-width: 100%;

  @media screen and (min-width: 648px) {
    max-width: 80%; // Mismo ancho que el texto del email en desktop
  }

  @media screen and (min-width: 1400px) {
    max-width: 500px; // O el valor que prefieras
  }
`;

const ErrorMsgComp = ({ error }) => (
  <p className="px-2 text-mn-caption1 leading-mn-caption1 tracking-mn-caption1 text-mn-error font-sans font-normal">
    {error}
  </p>
);

// Reglas de validación para los checkboxes (fuera del componente para evitar recreación)
const validationRules = {
  tac: [
    (value) =>
      !value ? "Es necesario que acepte la política de privacidad" : null,
  ],
  buro: [
    (value) =>
      !value
        ? "Es necesario que autorize la consulta del Buró de Crédito"
        : null,
  ],
};

const ValidationOTP = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpConfirm, setOtpConfirm] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const {
    rfc: rfcFromContext,
    idRegistro: idFromContext,
    email,
    setTacAccepted,
    setBuroAuthorized,
  } = useAuth();

  // Estados para los checkboxes - INICIALIZAR EN FALSE
  const [formData, setFormData] = useState({
    tac: false, // Cambiado a false
    buro: false, // Cambiado a false
  });
  const [errors, setErrors] = useState({
    tac: null,
    buro: null,
  });

  // Obtener valores de localStorage si no están en el contexto
  const rfc = rfcFromContext || localStorage.getItem("rfc");
  const idRegistro = idFromContext || localStorage.getItem("idRegistro");
  const savedEmail = email || localStorage.getItem("userEmail");

  const history = useHistory();
  const otpRefs = useRef([]);
  const otpConfirmRefs = useRef([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [showResendSuccess, setShowResendSuccess] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showValidationSuccess, setShowValidationSuccess] = useState(false);
  const [showAuthorizationSuccess, setShowAuthorizationSuccess] =
    useState(false);
  const [showModal, setShowModal] = useState(false);
  const errorTimeoutRef = useRef(null);
  const successTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const componentIsMounted = useRef(true);

  // Validar un campo específico
  const validate = useCallback((name, value) => {
    for (let i = 0; i < validationRules[name].length; i++) {
      const err = validationRules[name][i](value);
      if (err) return err;
    }
    return null;
  }, []); // Ahora no necesita dependencias porque validationRules está fuera del componente

  // Verificación de valores al montar
  useEffect(() => {
    if (!rfc || !idRegistro) {
      // Limpiar localStorage antes de redirigir
      localStorage.removeItem("idRegistro");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("tac_accepted");
      localStorage.removeItem("buro_authorized");
      window.location.href = "/";
    }
  }, [rfc, idRegistro]);

  // Iniciar contador para reenvío de OTP
  useEffect(() => {
    startCountdown();
    return () => {
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
    };
  }, []);

  const startCountdown = () => {
    if (!componentIsMounted.current) return;

    setCanResend(false);
    setCountdown(30);

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      if (!componentIsMounted.current) {
        clearInterval(countdownIntervalRef.current);
        return;
      }

      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Limpiar mensajes después de 10 segundos
  useEffect(() => {
    if (error) {
      errorTimeoutRef.current = setTimeout(() => {
        if (componentIsMounted.current) setError("");
      }, 10000);
    }

    if (successMessage) {
      successTimeoutRef.current = setTimeout(() => {
        if (componentIsMounted.current) setSuccessMessage("");
      }, 10000);
    }

    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, [error, successMessage]);

  const handleChange = (value, index, otpArray, setOtpArray, refs) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otpArray];
      newOtp[index] = value;
      setOtpArray(newOtp);

      if (error || successMessage) {
        setError("");
        setSuccessMessage("");
      }

      if (value && index < 5) {
        refs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index, otpArray, setOtpArray, refs) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  // Manejar cambios en los checkboxes
  const onInputChange = useCallback(
    (e) => {
      const { name, checked } = e.target;

      setFormData((data) => ({
        ...data,
        [name]: checked,
      }));

      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validate(name, checked),
      }));
    },
    [validate]
  );

  // Validar todos los campos antes de enviar
  const validateBeforeSubmit = useCallback(() => {
    const otpValue = otp.join("");
    const otpConfirmValue = otpConfirm.join("");

    // Validar OTPs
    if (otpValue !== otpConfirmValue) {
      setError("Los códigos no coinciden.");
      return false;
    }

    if (!otpValue || otpValue.length < 6) {
      setError("Por favor ingresa un código válido.");
      return false;
    }

    // Validar checkboxes - con mensajes más específicos
    const tacError = validate("tac", formData.tac);
    const buroError = validate("buro", formData.buro);

    if (tacError || buroError) {
      setErrors({
        tac: tacError,
        buro: buroError,
      });

      // Mostrar mensaje general si hay errores en checkboxes
      if (tacError && buroError) {
        setError("Debes aceptar ambos consentimientos para continuar.");
      } else if (tacError) {
        setError("Debes aceptar la política de privacidad para continuar.");
      } else if (buroError) {
        setError(
          "Debes autorizar la consulta de Buró de Crédito para continuar."
        );
      }

      return false;
    }

    // Limpiar errores si todo está bien
    setErrors({ tac: null, buro: null });
    setError("");
    return true;
  }, [otp, otpConfirm, formData, validate]);

  const handleApiError = (error) => {
    if (error.response) {
      if (
        error.response.data?.error?.includes("OTP") ||
        error.response.data?.error?.includes("código")
      ) {
        return "Código incorrecto o ya expirado";
      }
      return error.response.data?.error || "Error al procesar la solicitud";
    } else if (error.request) {
      return "Error de conexión. Verifica tu conexión e intenta nuevamente.";
    } else {
      return "Error al procesar la solicitud. Intenta nuevamente.";
    }
  };

  const handleAuthorization = async () => {
    if (!componentIsMounted.current) return;

    try {
      if (!idRegistro) {
        setApiError(
          "Faltan datos de autorización. Por favor inicia el proceso nuevamente."
        );
        return;
      }

      // ENVIAR los checkboxes al API (el API los requiere)
      const payload = {
        accion: "autorizacion",
        id: idRegistro,
        acepto_consulta_buro: formData.buro, // ← AGREGAR ESTO
        acepto_politica_privacidad: formData.tac, // ← AGREGAR ESTO
      };

      const response = await axios.post(
        `${process.env.REACT_APP_VISOR_SERVER}/webapi/validar_ciec`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!componentIsMounted.current) return;

      if (response.status === 200) {
        const mensaje = response.data?.mensaje?.toLowerCase?.() || "";

        if (mensaje.includes("autorizado previamente")) {
          setShowAuthorizationSuccess(true);
          setTimeout(() => {
            if (componentIsMounted.current) {
              window.location.href = "/";
            }
          }, 3000);
          return;
        }

        // Flujo normal - Guardamos localmente los checkboxes
        setTacAccepted(formData.tac);
        setBuroAuthorized(formData.buro);
        localStorage.setItem("tac_accepted", formData.tac.toString());
        localStorage.setItem("buro_authorized", formData.buro.toString());
        setShowAuthorizationSuccess(true);
        localStorage.removeItem("idRegistro");

        setTimeout(() => {
          if (componentIsMounted.current) {
            window.location.href = "/";
          }
        }, 3000);
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      setApiError(errorMessage);
    }
  };

  const handleSubmit = async () => {
    if (!componentIsMounted.current) return;

    // Validar todos los campos antes de proceder
    if (!validateBeforeSubmit()) {
      return;
    }

    setIsLoading(true);
    const otpValue = otp.join("");

    setError("");
    setSuccessMessage("");

    try {
      // Primero validamos el OTP (sin enviar checkboxes)
      const otpResponse = await axios.post(
        `${process.env.REACT_APP_VISOR_SERVER}/webapi/validar_ciec`,
        {
          accion: "validacion_otp",
          id: idRegistro,
          otp: otpValue,
        }
      );

      if (!componentIsMounted.current) return;

      if (otpResponse.data.mensaje === "OTP validado correctamente") {
        setShowValidationSuccess(true);

        // Guardamos los checkboxes solo localmente
        setTacAccepted(formData.tac);
        setBuroAuthorized(formData.buro);
        localStorage.setItem("tac_accepted", formData.tac.toString());
        localStorage.setItem("buro_authorized", formData.buro.toString());

        // Esperamos 2 segundos antes de proceder con la autorización
        setTimeout(() => {
          if (componentIsMounted.current) {
            handleAuthorization();
          }
        }, 2000);

        return;
      } else {
        setApiError("Código incorrecto o ya expirado");
        setCanResend(true);
        setCountdown(0);
      }
    } catch (error) {
      if (!componentIsMounted.current) return;
      const errorMessage = handleApiError(error);
      setApiError(errorMessage);
      setCanResend(true);
      setCountdown(0);
    } finally {
      if (componentIsMounted.current) {
        setIsLoading(false);
      }
    }
  };

  const reenviarOtp = async () => {
    if (!componentIsMounted.current) return;

    setIsResending(true);
    setError("");
    setSuccessMessage("");
    setApiError(null);

    try {
      await axios.post(
        `${process.env.REACT_APP_VISOR_SERVER}/webapi/validar_ciec`,
        {
          accion: "reenviar_otp",
          id: idRegistro,
        }
      );

      if (!componentIsMounted.current) return;

      setShowResendSuccess(true);
      setSuccessMessage(
        "Se ha reenviado un nuevo código al correo registrado."
      );
      setOtp(["", "", "", "", "", ""]);
      setOtpConfirm(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
      startCountdown();
    } catch (error) {
      if (!componentIsMounted.current) return;
      const errorMessage = handleApiError(error);

      setApiError(
        errorMessage ||
          "No fue posible reenviar el código. Intenta de nuevo más tarde."
      );
      setCanResend(true);
      setCountdown(0);

      // Verifica si el error indica que se debe salir del flujo
      const lowerMessage = errorMessage?.toLowerCase?.() || "";
      if (
        lowerMessage.includes("registro inválido") ||
        lowerMessage.includes("otp no reenviado") ||
        lowerMessage.includes("no autorizado") ||
        lowerMessage.includes("no válido")
      ) {
        setTimeout(() => {
          if (componentIsMounted.current) {
            window.location.href = "/";
          }
        }, 3000); // da 3 segundos para que vea el mensaje
      }
    } finally {
      if (componentIsMounted.current) {
        setIsResending(false);
      }
    }
  };

  const handleCorrectionClick = () => {
    localStorage.removeItem("idRegistro");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("tac_accepted");
    localStorage.removeItem("buro_authorized");
    history.push("/");
  };

  const handleResendSuccessClose = () => {
    if (componentIsMounted.current) {
      setShowResendSuccess(false);
    }
  };

  const handleApiErrorClose = () => {
    if (componentIsMounted.current) {
      setApiError(null);
    }
  };

  return (
    <AuthLayout>
      <v5.PopUp
        type="success"
        open={showValidationSuccess}
        style={{ borderRadius: "16px", textAlign: "center" }}
      >
        {`##### Validación exitosa \nSe validó el RFC ${rfc} con código OTP`}
      </v5.PopUp>

      <v5.PopUp
        type="success"
        open={showAuthorizationSuccess}
        style={{ borderRadius: "16px", textAlign: "center" }}
      >
        {`##### Felicidades \nSe envió la información del RFC ${rfc} y se validó con código OTP. \n\nRedirigiendo...`}
      </v5.PopUp>

      <v5.PopUp
        successButtonText="Aceptar"
        onSuccess={handleResendSuccessClose}
        open={showResendSuccess}
        style={{
          borderRadius: "16px",
          textAlign: "center",
        }}
      >
        {
          "## 📨 \n ##### Código reenviado \n Hemos enviado un nuevo código a tu correo electrónico. \n\nNo olvides revisar tu bandeja de correo no deseado."
        }
      </v5.PopUp>

      <v5.PopUp
        successButtonText="Entendido"
        type="error"
        onSuccess={handleApiErrorClose}
        open={!!apiError}
        style={{
          borderRadius: "16px",
          textAlign: "center",
        }}
      >
        {`##### Error \n${apiError}`}
      </v5.PopUp>

      <v5.PopUp
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Autorización para solicitar Reportes de Crédito"
        style={{
          borderRadius: "16px",
          maxWidth: "800px",
          padding: "2rem",
        }}
      >
        {"**Autorización para solicitar Reportes de Crédito Personas Físicas / Personas Morales Empresas Comerciales** \n\n --- \n" +
          "Por este conducto autorizo expresamente a APJUSTO SAPI DE CV o BANCO COVALTO SA IBM o JUSTO LEASE SAPI DE CV, para que por conducto de sus funcionarios facultados lleve a cabo Investigaciones, sobre mi comportamiento crediticio o el de la Empresa que represento en Sociedades de Información Crediticia. \n\n " +
          "Así mismo, declaro que conozco la naturaleza y alcance de la información que se solicitará, del uso que APJUSTO SAPI DE CV o BANCO COVALTO SA IBM o JUSTO LEASE SAPI DE CV, hará de tal información y de que ésta podrá realizar consultas periódicas sobre mi historial o el de la empresa que represento, consintiendo que esta autorización se encuentre vigente por un período de 3 años contrados a partir de su expedición y en todo caso durante el tiempo que se mantenga la relación jurídica. \n\n" +
          "En caso de que la solicitante sea una Persona Moral, declaro bajo protesta de decir verdad Ser Representante Legal de la empresa mencionada en esta autorización; manifestando que a la fecha de firma de la presente autorización los poderes no me han sido revocados, limitados, ni modificados en forma alguna."}
      </v5.PopUp>

      <Card>
        <v5.Typography
          color="mn-secondary"
          font="serif"
          variant="h3"
          weight="bold"
        >
          Validar código
        </v5.Typography>

        <EmailContainer>
          <v5.Typography
            color="mn-secondary"
            variant="subtitle1"
            weight="medium"
            style={{ marginTop: "0.25rem" }}
          >
            Enviamos un código a tu correo electrónico:
          </v5.Typography>
          <EmailText>{savedEmail}</EmailText>
          <CorrectionText>
            Si el correo no es correcto,{" "}
            <CorrectionLink onClick={handleCorrectionClick}>
              da click aquí para registrarte nuevamente
            </CorrectionLink>
          </CorrectionText>
        </EmailContainer>

        <v5.Typography
          as="label"
          variant="body2"
          weight="medium"
          color="text-primary"
          style={{ marginBottom: "0.25rem", display: "block" }}
        >
          Ingresa los 6 dígitos para verificar tu usuario *
        </v5.Typography>

        <OTPContainer>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {otp.map((digit, index) => (
              <v5.Input
                key={`otp-${index}`}
                required
                label=""
                maxLength="1"
                value={digit}
                ref={(el) => (otpRefs.current[index] = el)}
                onChange={(e) =>
                  handleChange(e.target.value, index, otp, setOtp, otpRefs)
                }
                onKeyDown={(e) => handleKeyDown(e, index, otp, setOtp, otpRefs)}
                style={{ width: "3rem", textAlign: "center" }}
              />
            ))}
          </div>
        </OTPContainer>

        <v5.Typography
          as="label"
          variant="body2"
          weight="medium"
          color="text-primary"
          style={{ marginBottom: "0.25rem", display: "block" }}
        >
          Confirma los 6 dígitos *
        </v5.Typography>

        <OTPContainer>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {otpConfirm.map((digit, index) => (
              <v5.Input
                key={`otp-confirm-${index}`}
                required
                label=""
                maxLength="1"
                value={digit}
                ref={(el) => (otpConfirmRefs.current[index] = el)}
                onChange={(e) =>
                  handleChange(
                    e.target.value,
                    index,
                    otpConfirm,
                    setOtpConfirm,
                    otpConfirmRefs
                  )
                }
                onKeyDown={(e) =>
                  handleKeyDown(
                    e,
                    index,
                    otpConfirm,
                    setOtpConfirm,
                    otpConfirmRefs
                  )
                }
                style={{ width: "3rem", textAlign: "center" }}
              />
            ))}
          </div>
        </OTPContainer>

        {/* Checkboxes de autorización - EXACTAMENTE IGUALES A LA PRIMERA VISTA */}
        <CheckboxContainer>
          <CheckboxWrapper>
            <v5.Checkbox
              id="tac"
              name="tac"
              checked={formData.tac}
              onChange={onInputChange}
              errorMessage={errors.tac}
              label={
                <v5.Markdown className={styles.markdownSecondary}>
                  {`Acepto la
                    <a
                      href="https://www.covalto.com/privacidad/"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="${styles.termsLink}"
                    >
                      política de privacidad
                    </a>
                  `}
                </v5.Markdown>
              }
            />
            {errors.tac && <ErrorMsgComp error={errors.tac} />}
          </CheckboxWrapper>

          <CheckboxWrapper>
            <v5.Checkbox
              id="buro"
              name="buro"
              checked={formData.buro}
              onChange={onInputChange}
              style={{ color: "red" }}
              errorMessage={errors.buro}
              label={
                <AutorizeBureu
                  onClick={(e) => {
                    setShowModal(true);
                    e.preventDefault();
                  }}
                >
                  Autorizo a BANCO COVALTO S.A., INSTITUCIÓN DE BANCA MÚLTIPLE,
                  la consulta de Buró de Crédito
                </AutorizeBureu>
              }
            />
            {errors.buro && <ErrorMsgComp error={errors.buro} />}
          </CheckboxWrapper>
        </CheckboxContainer>

        {error && (
          <v5.Typography
            color="error"
            variant="body2"
            style={{ marginTop: "1rem" }}
          >
            {error}
          </v5.Typography>
        )}

        {successMessage && (
          <v5.Typography
            color="success"
            variant="body2"
            style={{ marginTop: "1rem" }}
          >
            {successMessage}
          </v5.Typography>
        )}

        <ButtonContainer>
          {isLoading ? (
            <v5.Button
              className="opacity-75 cursor-wait"
              disabled
              variant="primary"
            >
              Cargando...
            </v5.Button>
          ) : (
            <v5.Button
              type="submit"
              variant="primary"
              onClick={handleSubmit}
              disabled={!formData.tac || !formData.buro}
            >
              Continuar
            </v5.Button>
          )}

          <v5.Button
            variant="light"
            onClick={reenviarOtp}
            disabled={!canResend || isResending}
          >
            {isResending ? (
              "Enviando..."
            ) : (
              <>
                Reenviar código
                {!canResend && <CountdownText>({countdown}s)</CountdownText>}
              </>
            )}
          </v5.Button>
        </ButtonContainer>
      </Card>
    </AuthLayout>
  );
};

export default ValidationOTP;
